'use client';

/**
 * Per-session status bar — field-for-field clone of the classic chat's
 * StatusBar: Model · thinking — ◉ctx% — $cost — ◷time — ♥keepalive — ¶msgs
 * │ 5h:% 7d:%  (rate limits are account-global, identical in every pane).
 * Model is clickable → per-session model switch.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getSessionUsage, onSessionUsage } from './useSessionChat';
import { estimateCost, formatDuration, formatModel, formatReset, formatTokens, modelFamilyRank } from '@/lib/model-format';

const GW = '/api/gw';

export function SessionStatusBar({ slug, main, streaming }: { slug: string; main?: boolean; streaming: boolean }) {
  const [info, setInfo] = useState<any>(null);
  const [startTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [, bumpUsage] = useState(0);
  const [rateLimit, setRateLimit] = useState<any>(null);
  const [showReset, setShowReset] = useState<'5h' | '7d' | null>(null);
  const [showCost, setShowCost] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [models, setModels] = useState<any[]>([]);
  const [switching, setSwitching] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const costRef = useRef<HTMLDivElement>(null);

  const poll = useCallback(() => {
    fetch(main ? `${GW}/session` : `${GW}/sessions/${slug}`)
      .then((r) => r.json())
      .then((d) => setInfo(main ? d : d.ok ? d.session : null))
      .catch(() => {});
    fetch(`${GW}/usage`).then((r) => r.json()).then((d) => { if (d?.updatedAt > 0) setRateLimit(d); }).catch(() => {});
  }, [slug, main]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);
  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(t);
  }, [startTime]);
  useEffect(() => onSessionUsage(() => { bumpUsage((x) => x + 1); poll(); }), [poll]);

  useEffect(() => {
    if (!showPicker) return;
    fetch(main ? `${GW}/models` : `${GW}/sessions/${slug}/models`)
      .then((r) => r.json())
      .then((d) => setModels(d.models || []))
      .catch(() => {});
  }, [showPicker, slug, main]);

  useEffect(() => {
    if (!showPicker && !showCost) return;
    const h = (e: MouseEvent) => {
      if (showPicker && pickerRef.current && !pickerRef.current.contains(e.target as Node)) setShowPicker(false);
      if (showCost && costRef.current && !costRef.current.contains(e.target as Node)) setShowCost(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showPicker, showCost]);

  const switchModel = useCallback(async (provider: string, id: string) => {
    setSwitching(true);
    try {
      const r = await fetch(main ? `${GW}/model` : `${GW}/sessions/${slug}/model`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider, id }),
      });
      const d = await r.json();
      if (d.ok) { poll(); setShowPicker(false); }
    } catch {}
    setSwitching(false);
  }, [slug, main, poll]);

  if (!info) return null;
  const cu = info.contextUsage || {};
  // in Auto mode show "Auto·<what it last ran>"; otherwise the fixed model
  const model = info.modelPolicy === 'auto'
    ? `Auto·${info.model ? formatModel(info.model.id) : '?'}`
    : info.model ? formatModel(info.model.id) : '—';
  const thinking = info.thinkingLevel || '—';
  const contextPct = Math.round(cu.percent ?? (cu.contextWindow ? (cu.tokens / cu.contextWindow) * 100 : 0));
  const u = getSessionUsage(slug);
  const cost = estimateCost(u, info.model?.id);
  const msgs = main ? info.messageCount : info.sessionMessageCount;

  return (
    <div className="flex items-center gap-1.5 px-1 pb-1.5 text-[10px] font-mono leading-none flex-wrap">
      <div className="relative inline-flex" ref={pickerRef}>
        <button type="button" onClick={() => setShowPicker(!showPicker)}
          className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors cursor-pointer"
          title="Click to switch this session's model">
          {model}
        </button>
        {showPicker && (
          <div className="absolute bottom-full left-0 mb-2 w-72 bg-gray-900 border border-white/10 rounded-lg shadow-xl shadow-black/40 z-50 max-h-[50vh] overflow-y-auto">
            <div className="px-3 py-2 border-b border-white/10 text-[10px] text-gray-500 uppercase tracking-wider">Switch model — this session only</div>
            {models.length === 0 ? (
              <div className="px-3 py-4 text-[11px] text-gray-600 text-center">Loading…</div>
            ) : (
              <div className="py-1">
                {models
                  .filter((m: any) => {
                    if (m.id.includes('latest')) return false;
                    if (m.id.match(/-\d{8}$/)) {
                      const base = m.id.replace(/-\d{8}$/, '');
                      return !models.some((o: any) => o.id === base);
                    }
                    return true;
                  })
                  .sort((a: any, b: any) => {
                    if (a.id === 'auto') return -1; if (b.id === 'auto') return 1; // Auto pinned top
                    const af = modelFamilyRank(a.id);
                    const bf = modelFamilyRank(b.id);
                    if (af !== bf) return af - bf;
                    return b.id.localeCompare(a.id);
                  })
                  .map((m: any) => {
                    const isAuto = m.id === 'auto';
                    const isCurrent = isAuto ? info.modelPolicy === 'auto' : (info.modelPolicy !== 'auto' && info.model?.id === m.id);
                    return (
                      <button key={m.id} type="button" disabled={switching || isCurrent}
                        onClick={() => switchModel(m.provider, m.id)}
                        title={isAuto ? 'Heuristic router: Fable for reasoning, Sonnet for cheap ops — picks per message' : undefined}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${isCurrent ? 'bg-cyan-500/10 border-l-2 border-cyan-400' : 'border-l-2 border-transparent hover:bg-white/5'} ${isAuto ? 'border-b border-white/10' : ''}`}>
                        <span className={`flex-1 text-[11px] font-mono ${isCurrent ? 'text-cyan-300' : isAuto ? 'text-emerald-300' : 'text-gray-300'}`}>{isAuto ? '🤖 Auto (Fable⇄Sonnet)' : formatModel(m.id)}</span>
                        {!isAuto && m.reasoning && <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-300">think</span>}
                        {!isAuto && <span className="text-[9px] text-gray-600">{m.contextWindow >= 1_000_000 ? '1M' : `${Math.round(m.contextWindow / 1000)}k`}</span>}
                      </button>
                    );
                  })}
              </div>
            )}
            {switching && <div className="px-3 py-2 border-t border-white/10 text-[10px] text-cyan-400 text-center">Switching…</div>}
          </div>
        )}
      </div>
      <span className="text-gray-700">•</span>
      <span className="text-gray-500">{thinking}</span>
      <span className="text-gray-700">─</span>
      <span className={contextPct > 90 ? 'text-red-400 animate-pulse' : contextPct > 70 ? 'text-amber-400' : 'text-emerald-400'}>◉{contextPct}%</span>
      {contextPct > 70 && <span className="text-[9px] text-amber-400/70">breathe</span>}
      <span className="text-gray-700">─</span>
      <div className="relative inline-flex" ref={costRef}>
        <button type="button" onClick={() => setShowCost(!showCost)}
          className="text-green-500/70 hover:text-green-400 transition-colors cursor-pointer">
          ${cost.toFixed(2)}
        </button>
        {showCost && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-white/10 rounded-lg shadow-xl whitespace-nowrap z-50">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Token usage — this session</div>
            <div className="space-y-1 text-[11px]">
              <div className="flex justify-between gap-4"><span className="text-gray-500">In</span><span className="text-gray-300">{formatTokens(u.input)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">Out</span><span className="text-gray-300">{formatTokens(u.output)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">Cache read</span><span className="text-gray-300">{formatTokens(u.cacheRead)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-gray-500">Cache write</span><span className="text-gray-300">{formatTokens(u.cacheWrite)}</span></div>
              <div className="border-t border-white/10 pt-1 flex justify-between gap-4"><span className="text-gray-400">Total</span><span className="text-green-400 font-semibold">{formatTokens(u.input + u.output + u.cacheRead + u.cacheWrite)}</span></div>
            </div>
          </div>
        )}
      </div>
      <span className="text-gray-700">─</span>
      <span className="text-gray-600">◷</span>
      <span className="text-gray-400">{formatDuration(elapsed)}</span>
      <span className="text-gray-700">─</span>
      <span className="text-pink-400/70" title="Soma keepalive runs in every session">♥on</span>
      <span className="text-gray-700">─</span>
      <span className="text-gray-600">¶</span>
      <span className="text-gray-400">{msgs ?? 0}</span>
      {(streaming || info.isStreaming) && (
        <>
          <span className="text-gray-700">─</span>
          <span className="text-cyan-400 animate-pulse">●</span>
        </>
      )}
      {rateLimit?.updatedAt > 0 && (
        <>
          <span className="text-gray-700 ml-1">│</span>
          {rateLimit.session && (
            <button type="button" onClick={() => setShowReset(showReset === '5h' ? null : '5h')}
              className={`${rateLimit.session.percent > 80 ? 'text-red-400' : rateLimit.session.percent > 50 ? 'text-amber-400' : 'text-gray-400'} hover:text-white cursor-pointer`}>
              {showReset === '5h' && rateLimit.session.resetsAt ? `5h:${formatReset(rateLimit.session.resetsAt)}` : `5h:${rateLimit.session.percent}%`}
            </button>
          )}
          {rateLimit.weekAll && (
            <button type="button" onClick={() => setShowReset(showReset === '7d' ? null : '7d')}
              className={`${rateLimit.weekAll.percent > 80 ? 'text-red-400' : rateLimit.weekAll.percent > 50 ? 'text-amber-400' : 'text-gray-400'} hover:text-white cursor-pointer`}>
              {showReset === '7d' && rateLimit.weekAll.resetsAt ? `7d:${formatReset(rateLimit.weekAll.resetsAt)}` : `7d:${rateLimit.weekAll.percent}%`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
